import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled, { css, useTheme } from "styled-components";
import { Environment } from "@core/config/environment";
import { Media } from "@domain/media/media";
import { useIsMobile } from "@utils/hooks";
import { gap, smallMobile } from "@ui/styles/utils";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { AssetManagerIcon, CameraIcon, ImageIcon, PdfIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { FileUpload } from "@ui/components/mediaEditor/fileUpload";

interface EditPostMediasProps extends React.HTMLAttributes<HTMLDivElement> {
	onImagesUploaded: (images: Media[]) => void;
	onPdfOrVideoUploaded: (pdf: Media) => void;
	imagesOrAssetManagerDisabled?: boolean;
	pdfDisabled?: boolean;
	onAssetBrowserOpened: () => void;
	onVideoUploadStart?: () => void;
	onVideoProgress?: (progress: number) => void;
	onUploadCancel?: () => void;
}
export const EditPostMedias: React.FC<EditPostMediasProps> = ({
	onImagesUploaded,
	onPdfOrVideoUploaded,
	imagesOrAssetManagerDisabled,
	pdfDisabled,
	onAssetBrowserOpened,
	onVideoUploadStart,
	onVideoProgress,
	onUploadCancel,
	...htmlProps
}) => {
	const theme = useTheme();
	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState("");
	const isMobile = useIsMobile();

	return (
		<Root {...htmlProps}>
			<Container>
				<StyledFileUpload
					disabled={uploading || imagesOrAssetManagerDisabled}
					showInlineError={false}
					onErrorChange={setUploadError}
					onMultipleUpload={(images) => {
						onImagesUploaded(images);
						setUploading(false);
					}}
					onUploadStart={() => setUploading(true)}
				>
					{(_, loading) => (
						<MediaButton compact iconColor={Environment.APP_VARIANT === "jaangal" ? "#E83536" : theme.colors.secondary}>
							{loading ? <ClipLoader size={18} /> : <ImageIcon />}
							{!isMobile && <PrimaryMedium>{I18n.t("post.image.add")}</PrimaryMedium>}
						</MediaButton>
					)}
				</StyledFileUpload>
				<StyledFileUpload
					disabled={uploading || pdfDisabled}
					type="pdf"
					showInlineError={false}
					onErrorChange={setUploadError}
					onUploadStart={() => setUploading(true)}
					onUpload={(asset) => {
						onPdfOrVideoUploaded(asset);
						setUploading(false);
					}}
				>
					{(_, loading) => (
						<MediaButton compact iconColor={theme.colors.orange}>
							{loading ? <ClipLoader size={18} /> : <PdfIcon />}
							{!isMobile && <PrimaryMedium>{I18n.t("post.pdf.add")}</PrimaryMedium>}
						</MediaButton>
					)}
				</StyledFileUpload>
				<StyledFileUpload
					disabled={uploading || pdfDisabled}
					showInlineError={false}
					onErrorChange={setUploadError}
					onUploadStart={() => {
						onVideoUploadStart?.();
						setUploading(true);
					}}
					onUpload={(asset) => {
						onPdfOrVideoUploaded(asset);
						setUploading(false);
					}}
					onError={() => {
						setUploading(false);
						onUploadCancel?.();
					}}
					onUploadProgress={onVideoProgress}
					type="video"
				>
					{(_, loading) => (
						<MediaButton compact iconColor={theme.colors.secondary}>
							{loading ? <ClipLoader size={18} /> : <CameraIcon />}
							{!isMobile && <PrimaryMedium>{I18n.t("post.video.add")}</PrimaryMedium>}
						</MediaButton>
					)}
				</StyledFileUpload>
				<Separator />
				<MediaManagerWrapper
					onClick={() => onAssetBrowserOpened()}
					disabled={uploading || imagesOrAssetManagerDisabled}
				>
					<MediaButton iconColor={theme.colors.secondary}>
						<AssetManagerIcon />
						<PrimaryMedium>{I18n.t("asset_manager.title")}</PrimaryMedium>
					</MediaButton>
				</MediaManagerWrapper>
			</Container>
			{uploadError ? <UploadError role="alert">{uploadError}</UploadError> : null}
		</Root>
	);
};

const Root = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
`;

const Container = styled.div`
	${row(10, "flex-start", "center")};
	margin-left: -10px;
`;

const UploadError = styled.div`
	margin-top: 4px;
	font-size: 0.875rem;
	line-height: 1.3;
	color: ${({ theme }) => theme.colors.red};
`;

const iconWrapperStyle = css<{ disabled?: boolean }>`
	cursor: pointer;
	border-radius: 7px;
	transition: background-color linear 0.1s;
	&:hover {
		background-color: ${({ theme }) => theme.colors.bodyBackground};
	}

	${({ disabled }) =>
		disabled &&
		css`
			opacity: 0.2;
			pointer-events: none;
		`}
`;

const StyledFileUpload = styled(FileUpload)<{ disabled?: boolean }>`
	${iconWrapperStyle}
	@media ${smallMobile} {
		margin-right: 3px !important;
	}
`;
const MediaManagerWrapper = styled.div<{ disabled?: boolean }>`
	${iconWrapperStyle};
	@media ${smallMobile} {
		margin-right: 3px !important;
	}
`;

const MediaButton = styled.div.withConfig({
	shouldForwardProp: (prop) => !["iconColor", "compact"].includes(prop),
})<{ iconColor: string; compact?: boolean }>`
	padding: 10px;
	${row(10, "flex-start", "center")};

	@media (max-width: 1140px) {
		${({ compact }) => compact && gap(0)};
		> :last-child {
			${({ compact }) => compact && "display: none;"}
			${({ theme }) => theme.textStyles.text};
		}
	}

	@media ${smallMobile} {
		padding: 6px;
	}

	> svg {
		width: 18px;
		height: 18px;
		path {
			fill: ${({ iconColor }) => iconColor};
		}
	}
`;

const Separator = styled.div`
	flex-shrink: 0;
	background-color: ${({ theme }) => theme.colors.gray};
	width: 1px;
	height: 28px;
	align-self: center;
	@media ${smallMobile} {
		margin-right: 3px !important;
	}
`;
